import { createContext, ReactNode, useContext, useRef, useState } from "react";
import {
  Button,
  Dialog,
  TextField,
  TTextFieldProps,
  Typography,
} from "@/components/kit";
import { TDialogProps } from "@/components/kit/Dialog";
import { TButtonProps } from "@/components/kit/Button";
import Deferred from "@/utilize/DeferredPromise.ts";
import { useTranslation } from "react-i18next";

type TConfig = {
  title: ReactNode;
  description: ReactNode;
  abortButtonProps?: Omit<TButtonProps, "children">;
  confirmButtonProps?: Omit<TButtonProps, "children">;
  abortText?: string;
  confirmText?: string;
  dialogProps?: Omit<TDialogProps, "open" | "handler" | "children">;
  textToConfirm?: string;
  textFieldProps?: TTextFieldProps;
};
type TReturnData = Promise<boolean>;
type TContextData = { confirm: (config: TConfig) => TReturnData };
const ConfirmationContext = createContext<TContextData>({} as TContextData);

export default ConfirmationContext;

export function ConfirmationContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(false);
  const [configs, setConfigs] = useState<TConfig>({} as TConfig);
  const [textConfirmation, setTextConfirmation] = useState<string>("");
  const deferredPromise = useRef<Deferred<boolean>>();
  const { t } = useTranslation("common");
  const confirm = async (config: TConfig): TReturnData => {
    setConfigs(config);
    setOpen(true);
    deferredPromise.current = new Deferred<boolean>();
    return deferredPromise.current;
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => setConfigs({} as TConfig), 500);
    setTextConfirmation("");
  };

  const accept = () => {
    deferredPromise.current?.resolve(true);
    close();
  };

  const abort = () => {
    deferredPromise.current?.resolve(false);
    close();
  };

  return (
    <ConfirmationContext.Provider
      value={{
        confirm,
      }}
    >
      {children}
      <Dialog
        mainClassName={"!p-0"}
        titleClassName="pt-4 px-6"
        bodyClassName="px-6 !m-0 pb-4"
        footerClassName="px-6 py-4"
        {...configs.dialogProps}
        title={configs.title}
        open={open}
        handler={abort}
        body={
          <Typography variant={"BodyPlus1-Regular"} className={"text-left"}>
            {configs.description}
          </Typography>
        }
      >
        <div className={"w-full"}>
          {!!configs.textToConfirm && (
            <div className={"flex flex-col mb-3 w-full gap-3"}>
              <div
                dangerouslySetInnerHTML={{
                  __html: t("text_confirmation_description", {
                    text: configs.textToConfirm,
                  }),
                }}
              />
              <TextField
                {...configs.textFieldProps}
                initialFocus={true}
                onChange={(e) => setTextConfirmation(e.target.value)}
                value={textConfirmation}
              />
            </div>
          )}
          <div className={"flex items-center gap-3  justify-end"}>
            <Button
              variant={"outlined"}
              {...configs.abortButtonProps}
              onClick={abort}
            >
              {configs.abortText || t("cancel")}
            </Button>
            <Button
              {...configs.confirmButtonProps}
              disabled={
                !!configs.textToConfirm &&
                textConfirmation !== configs.textToConfirm
              }
              className="bg-red-50"
              onClick={accept}
            >
              {configs.confirmText || t("confirm")}
            </Button>
          </div>
        </div>
      </Dialog>
    </ConfirmationContext.Provider>
  );
}

export const useConfirmation = () => {
  return useContext(ConfirmationContext);
};
